/**
 * Campaign Enums
 *
 * Enums for email and voice campaign management.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// CAMPAIGN STATUS
// ============================================================================

/**
 * Campaign lifecycle status
 * Used by: Email campaigns, Voice campaigns
 */
export enum CampaignStatus {
  /** Campaign is being created/edited */
  DRAFT = 'draft',
  /** Campaign is scheduled for future execution */
  SCHEDULED = 'scheduled',
  /** Campaign is currently running */
  ACTIVE = 'active',
  /** Campaign execution is paused */
  PAUSED = 'paused',
  /** Campaign has finished executing */
  COMPLETED = 'completed',
}

/** Zod schema for CampaignStatus validation */
export const CampaignStatusSchema = z.nativeEnum(CampaignStatus);

/** Array of all campaign statuses for iteration */
export const CAMPAIGN_STATUSES = Object.values(CampaignStatus);

// ============================================================================
// CAMPAIGN TYPE
// ============================================================================

/**
 * Email campaign types
 */
export enum EmailCampaignType {
  PRODUCT_ANNOUNCEMENT = 'product_announcement',
  NURTURE = 'nurture',
  RE_ENGAGEMENT = 're_engagement',
  REFERRAL_PARTNER = 'referral_partner',
}

/** Zod schema for EmailCampaignType validation */
export const EmailCampaignTypeSchema = z.nativeEnum(EmailCampaignType);

/** Array of all email campaign types for iteration */
export const EMAIL_CAMPAIGN_TYPES = Object.values(EmailCampaignType);

// ============================================================================
// RECIPIENT TYPE
// ============================================================================

/**
 * Campaign recipient types
 */
export enum CampaignRecipientType {
  LEAD = 'lead',
  PARTNER = 'partner',
  SEGMENT = 'segment',
}

/** Zod schema for CampaignRecipientType validation */
export const CampaignRecipientTypeSchema = z.nativeEnum(CampaignRecipientType);

// ============================================================================
// AUTONOMY LEVEL
// ============================================================================

/**
 * Voice campaign AI autonomy levels
 */
export enum VoiceAutonomyLevel {
  /** Fully automated AI calls */
  FULL_AI = 'full_ai',
  /** AI with human handoff capability */
  HYBRID = 'hybrid',
  /** AI assists human agents */
  AI_ASSIST = 'ai_assist',
  /** Human-only calls */
  HUMAN_ONLY = 'human_only',
}

/** Zod schema for VoiceAutonomyLevel validation */
export const VoiceAutonomyLevelSchema = z.nativeEnum(VoiceAutonomyLevel);

// ============================================================================
// CALL STATUS
// ============================================================================

/**
 * Voice call status
 */
export enum CallStatus {
  PENDING = 'pending',
  QUEUED = 'queued',
  RINGING = 'ringing',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  NO_ANSWER = 'no_answer',
  BUSY = 'busy',
  VOICEMAIL = 'voicemail',
}

/** Zod schema for CallStatus validation */
export const CallStatusSchema = z.nativeEnum(CallStatus);

/** Array of all call statuses for iteration */
export const CALL_STATUSES = Object.values(CallStatus);
